import { SearchResponse } from "@algolia/client-search"
import { Document } from "@contentful/rich-text-types"
import { Entry } from "contentful"
import Time from "decentraland-gatsby/dist/utils/date/Time"

import { CustomHighlight } from "../components/Blog/Search/CustomeHighlight"
import { SearchMatches } from "../components/Blog/Search/Search"
import {
  BlogPostAuthorFields,
  BlogPostAuthorFieldsCurated,
  BlogPostCategoryFields,
  BlogPostCategoryFieldsCurated,
  BlogPostFields,
  BodyImageReference,
} from "../hooks/useBlogContentfulQuery"
import { AlgoliaSearch } from "./algolia"
import locations from "./locations"

import type { IBlogPostFields } from "../@types/contentful"

// TODO: transform to a hook
export const searchFormatWithPost = (
  algoliaResponse: SearchResponse<AlgoliaSearch>,
  getBlogPost: (id: string) => BlogPostFields | undefined
) =>
  algoliaResponse.hits
    .map<SearchMatches | null>((hit) => {
      const blogPost = getBlogPost(hit.id)

      if (!blogPost) {
        return null
      }
      const imageUrl = blogPost.image.url

      return {
        url: locations.blog(blogPost.category!.id, hit.id),
        image: imageUrl,
        title: CustomHighlight({ attribute: "title", hit: hit }) || [],
        description:
          CustomHighlight({
            attribute: "description",
            hit: hit,
          }) || [],
      }
    })
    .filter((match) => match !== null)

export type BlogPostAttributes = {
  categoryId: string
  categoryTitle: string
  categoryUrl: string
  categoryDescription?: string
  categoryImage?: string
  description: string
  id: string
  image: string
  publishedDate: string
  title: string
  url: string
  body: Document
  bodyReferences?: BodyImageReference[]
  authorId: string
  authorTitle: string
  authorDescription?: string
  authorImage: string
}

export const transformPostListToBlogPostAttributes = (
  posts: BlogPostFields[]
): BlogPostAttributes[] =>
  posts.map((post) => {
    return {
      id: post.id!,
      url: locations.blog(post.category!.id, post.id!),
      image: post.image.url,
      publishedDate: Time.utc(post.publishedDate).format("MMM DD, YYYY"),
      categoryId: post.category!.id,
      categoryTitle: post.category!.title,
      categoryUrl: locations.category(post.category!.id),
      title: post.title,
      description: post.description.description,
      body: JSON.parse(post.body.raw) as Document,
      bodyReferences: post.body.references,
      authorId: post.author!.id,
      authorTitle: post.author!.title,
      authorImage: post.author!.image.url,
    }
  })

export const transformEntryToBlogPostAttributes = (
  post: Entry<IBlogPostFields>
): BlogPostAttributes => ({
  id: post.sys.id,
  title: post.fields.title,
  description: post.fields.description,
  publishedDate: post.fields.publishedDate,
  body: post.fields.body,
  image: post.fields.image.fields.file.url,
  categoryId: post.fields.category.sys.id,
  categoryTitle: post.fields.category.fields.title,
  categoryUrl: locations.category(post.fields.category.sys.id),
  url: locations.blog(post.fields.category.sys.id, post.sys.id),
  categoryDescription: post.fields.category.fields.description,
  authorId: post.fields.author.sys.id,
  authorTitle: post.fields.author.fields.title,
  authorDescription: post.fields.author.fields.description,
  authorImage: post.fields.author.fields.image.fields.file.url,
})

export const transformBlogPostCategoryFieldsToCurated = (
  category: BlogPostCategoryFields
): BlogPostCategoryFieldsCurated => ({
  id: category.id,
  title: category.title,
  description: category.description.description,
  image: category.image,
  isShownInMenu: category.isShownInMenu,
})

export const transformBlogPostAuthorFieldsToCurated = (
  author: BlogPostAuthorFields
): BlogPostAuthorFieldsCurated => ({
  id: author.id,
  title: author.title,
  description: author.description.description,
  image: author.image,
})
